import {
	CSSProperties,
	FC,
	useEffect,
	useMemo,
	useState
} from 'react'
import { useTranslation } from 'react-i18next'
import {
	useNavigate,
	useParams,
	useSearchParams
} from 'react-router-dom'
import {
	Table,
	Badge,
	Button,
	Space,
	Tooltip,
	Spin,
	Empty,
	Card,
	Row,
	Col,
	Select
} from 'antd'
import {
	ReloadOutlined,
	DownloadOutlined,
	ArrowLeftOutlined
} from '@ant-design/icons'
import { loggingStore } from '../store/logging'
import { carrierStore } from '../store/carrier'
import { RNCarriers, RNLogging } from '../helpers/routeNames'
import { Logging as LoggingType } from '../helpers/types/logging'
import {
	getLoggingCodeValue,
	getLoggingIsError,
	getLoggingNotify,
	getLoggingSource,
	getLoggingType
} from '../helpers/logging'

interface StackedLog {
	key: string
	code: string
	type: string
	source: string
	text: string
	isError: boolean
	notify: boolean
	count: number
	firstOccurrence: string
	lastOccurrence: string
	entries: LoggingType[]
}

const isTruthyQueryValue = (value: string | null): boolean => {
	if (!value) return false
	return ['true', '1', 'yes'].includes(value.toLowerCase())
}

const sortLogsByTimeDesc = (logs: LoggingType[]): LoggingType[] => {
	return [...logs].sort((a, b) => {
		const timeDiff =
			new Date(b.happenedAt).getTime() -
			new Date(a.happenedAt).getTime()

		if (timeDiff !== 0) return timeDiff
		return b.id - a.id
	})
}

function stackLogs(logs: LoggingType[]): StackedLog[] {
	const orderedLogs = [...logs].sort((a, b) => {
		const timeDiff =
			new Date(b.happenedAt).getTime() -
			new Date(a.happenedAt).getTime()

		if (timeDiff !== 0) return timeDiff
		return b.id - a.id
	})
	const grouped: StackedLog[] = []

	for (const log of orderedLogs) {
		const code = getLoggingCodeValue(log)
		const type = getLoggingType(log)
		const source = getLoggingSource(log)
		const signature = `${type}||${code}||${source}||${log.text}`
		const lastGroup = grouped[grouped.length - 1]

		if (
			lastGroup &&
			`${lastGroup.type}||${lastGroup.code}||${lastGroup.source}||${lastGroup.text}` ===
				signature
		) {
			lastGroup.count++
			lastGroup.entries.push(log)
			lastGroup.firstOccurrence = String(log.happenedAt)
			continue
		}

		grouped.push({
			key: `${signature}||${log.id}`,
			code,
			type,
			source,
			text: log.text,
			isError: getLoggingIsError(log),
			notify: getLoggingNotify(log),
			count: 1,
			firstOccurrence: String(log.happenedAt),
			lastOccurrence: String(log.happenedAt),
			entries: [log]
		})
	}

	return grouped
}

const Logging: FC = () => {
	const { t } = useTranslation()
	const { id } = useParams<{ id: string }>()
	const navigate = useNavigate()
	const [searchParams, setSearchParams] = useSearchParams()
	const { loggings: liveLoggings, fetchCarrierLoggings } =
		loggingStore()
	const { carriers } = carrierStore()
	const [loading, setLoading] = useState(false)
	const [carrierLogs, setCarrierLogs] = useState<LoggingType[]>([])
	const [filteredType, setFilteredType] = useState<string | null>(
		null
	)
	const [filteredSource, setFilteredSource] = useState<
		string | null
	>(null)

	const carrierId = id ? +id : null
	const onlyErrors = useMemo(
		() => isTruthyQueryValue(searchParams.get('onlyErrors')),
		[searchParams]
	)

	useEffect(() => {
		if (!carrierId || isNaN(carrierId)) {
			navigate(RNCarriers)
			return
		}

		const loadLogs = async () => {
			setLoading(true)
			try {
				const data = await fetchCarrierLoggings(carrierId, {
					onlyErrors
				})
				setCarrierLogs(sortLogsByTimeDesc(data))
			} finally {
				setLoading(false)
			}
		}

		void loadLogs()
	}, [carrierId, navigate, onlyErrors, fetchCarrierLoggings])

	useEffect(() => {
		if (!carrierId || isNaN(carrierId)) {
			return
		}

		const nextLiveLogs = liveLoggings.filter((log) => {
			if (log.carrierId !== carrierId) return false
			if (onlyErrors && !getLoggingIsError(log)) return false
			return true
		})

		if (nextLiveLogs.length === 0) {
			return
		}

		setCarrierLogs((current) => {
			const existingIds = new Set(current.map((log) => log.id))
			const unseenLogs = nextLiveLogs.filter(
				(log) => !existingIds.has(log.id)
			)

			if (unseenLogs.length === 0) {
				return current
			}

			return sortLogsByTimeDesc([...unseenLogs, ...current])
		})
	}, [carrierId, liveLoggings, onlyErrors])

	const logs = carrierLogs

	const filteredLogs = logs.filter((log) => {
		if (filteredType && getLoggingType(log) !== filteredType)
			return false
		if (
			filteredSource &&
			getLoggingSource(log) !== filteredSource
		)
			return false
		return true
	})

	const stackedLogs = stackLogs(filteredLogs)

	const uniqueTypes = Array.from(
		new Set(
			logs.map((log) => getLoggingType(log)).filter(Boolean)
		)
	)
	const uniqueSources = Array.from(
		new Set(
			logs.map((log) => getLoggingSource(log)).filter(Boolean)
		)
	)

	const carrier = carriers.find((c) => c.id === carrierId)

	const handleRefresh = () => {
		if (carrierId) {
			setLoading(true)
			fetchCarrierLoggings(carrierId, {
				onlyErrors
			})
				.then((data) => {
					setCarrierLogs(sortLogsByTimeDesc(data))
				})
				.finally(() => setLoading(false))
		}
	}

	const handleOnlyErrorsToggle = () => {
		const nextParams = new URLSearchParams(searchParams)

		if (onlyErrors) {
			nextParams.delete('onlyErrors')
		} else {
			nextParams.set('onlyErrors', 'true')
		}

		setSearchParams(nextParams)
	}

	const handleExport = () => {
		const dataStr = JSON.stringify(filteredLogs, null, 2)
		const dataBlob = new Blob([dataStr], {
			type: 'application/json'
		})
		const url = URL.createObjectURL(dataBlob)
		const link = document.createElement('a')
		link.href = url
		link.download = `logs_${carrierId}_${new Date().getTime()}.json`
		link.click()
		URL.revokeObjectURL(url)
	}

	const pageStyle: CSSProperties = {
		height: 'calc(100vh - 70px)',
		overflow: 'auto',
		backgroundColor: '#f5f5f5',
		padding: '16px'
	}

	const headerStyle: CSSProperties = {
		marginBottom: '16px',
		backgroundColor: 'white',
		padding: '16px',
		borderRadius: '4px',
		boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
	}

	const filterCardStyle: CSSProperties = {
		marginBottom: '16px',
		backgroundColor: 'white',
		borderRadius: '4px',
		boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
	}

	const tableContainerStyle: CSSProperties = {
		backgroundColor: 'white',
		borderRadius: '4px',
		boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
		padding: '0'
	}

	if (!carrierId || isNaN(carrierId)) {
		return (
			<div style={pageStyle}>
				<Empty
					description={t('Redirecting')}
					style={{ paddingTop: '100px' }}
				/>
			</div>
		)
	}

	return (
		<div style={pageStyle}>
			<div style={headerStyle}>
				<div
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'flex-start'
					}}>
					<div style={{ flex: 1 }}>
						<h1 style={{ margin: '0 0 8px 0' }}>
							{t('EventLog')} •{' '}
							{carrier?.name || `#${carrierId}`}
						</h1>
						<p
							style={{
								margin: 0,
								color: '#666',
								fontSize: '12px'
							}}>
							{t('TotalEvents')}:{' '}
							<strong>{filteredLogs.length}</strong> (
							{t('UniqueEvents')}:{' '}
							<strong>{stackedLogs.length}</strong>)
							{filteredType || filteredSource
								? ` (${t('Filtered')})`
								: ''}
						</p>
					</div>
					<Space style={{ marginLeft: '16px' }}>
						<Button
							danger={onlyErrors}
							type={onlyErrors ? 'primary' : 'default'}
							disabled={
								!onlyErrors &&
								!carrier?.activeProblemsCount
							}
							onClick={handleOnlyErrorsToggle}>
							{onlyErrors
								? t('All')
								: `${t('IsError')} (${carrier?.activeProblemsCount || 0})`}
						</Button>
						<Tooltip title={t('Back')}>
							<Button
								icon={<ArrowLeftOutlined />}
								onClick={() => window.history.back()}
							/>
						</Tooltip>
						<Tooltip title={t('Loggings')}>
							<Button
								icon={<ArrowLeftOutlined />}
								onClick={() => navigate(RNLogging)}>
								{t('All')}
							</Button>
						</Tooltip>
					</Space>
				</div>
			</div>

			<Card style={filterCardStyle}>
				<Row gutter={[16, 16]}>
					<Col xs={24} sm={12} md={8}>
						<div>
							<label
								style={{
									display: 'block',
									marginBottom: '8px',
									fontSize: '12px',
									fontWeight: 600
								}}>
								{t('Type')}
							</label>
							<Select
								placeholder={`All Types (${uniqueTypes.length})`}
								allowClear
								style={{ width: '100%' }}
								value={filteredType}
								onChange={setFilteredType}
								options={uniqueTypes.map((type) => ({
									label: type,
									value: type
								}))}
							/>
						</div>
					</Col>
					<Col xs={24} sm={12} md={8}>
						<div>
							<label
								style={{
									display: 'block',
									marginBottom: '8px',
									fontSize: '12px',
									fontWeight: 600
								}}>
								{t('Source')}
							</label>
							<Select
								placeholder={`All Sources (${uniqueSources.length})`}
								allowClear
								style={{ width: '100%' }}
								value={filteredSource}
								onChange={setFilteredSource}
								options={uniqueSources.map(
									(source) => ({
										label: source,
										value: source
									})
								)}
							/>
						</div>
					</Col>
					<Col xs={24} sm={12} md={8}>
						<div>
							<label
								style={{
									display: 'block',
									marginBottom: '8px',
									fontSize: '12px',
									fontWeight: 600
								}}>
								{t('Actions')}
							</label>
							<Space>
								<Tooltip title={t('Refresh')}>
									<Button
										icon={<ReloadOutlined />}
										onClick={handleRefresh}
										loading={loading}
										type="primary"
									/>
								</Tooltip>
								<Tooltip title={t('Export')}>
									<Button
										icon={<DownloadOutlined />}
										onClick={handleExport}
										disabled={
											filteredLogs.length === 0
										}
									/>
								</Tooltip>
							</Space>
						</div>
					</Col>
				</Row>
			</Card>

			<div style={tableContainerStyle}>
				<Spin spinning={loading}>
					{filteredLogs.length === 0 ? (
						<Empty
							description={t('NoData')}
							style={{ padding: '40px 0' }}
						/>
					) : (
						<Table<StackedLog>
							dataSource={stackedLogs}
							rowKey="key"
							expandable={{
								expandedRowRender: (record) => (
									<Table<LoggingType>
										dataSource={record.entries}
										rowKey="id"
										pagination={false}
										size="small"
										bordered={false}
										scroll={{ x: 'max-content' }}
										columns={[
											{
												title: '№',
												key: 'id',
												width: 60,
												align: 'center',
												render: (
													_: unknown,
													row: LoggingType
												) => row.lid ?? row.id
											},
											{
												title: t('Time'),
												dataIndex:
													'happenedAt',
												key: 'happenedAt',
												width: 180,
												render: (v: string) =>
													new Date(
														v
													).toLocaleString(
														'ru'
													)
											},
											// {
											// 	title: t('Code'),
											// 	key: 'code',
											// 	width: 140,
											// 	render: (
											// 		_: unknown,
											// 		row: LoggingType
											// 	) =>
											// 		getLoggingCodeValue(
											// 			row
											// 		) || '-'
											// },
											{
												title: t('Type'),
												key: 'type',
												width: 120,
												render: (
													_: unknown,
													row: LoggingType
												) =>
													getLoggingType(
														row
													) || '-'
											},
											{
												title: t('Source'),
												key: 'source',
												width: 145,
												render: (
													_: unknown,
													row: LoggingType
												) =>
													getLoggingSource(
														row
													) || '-'
											},
											// {
											// 	title: t('IsError'),
											// 	key: 'isError',
											// 	width: 80,
											// 	render: (
											// 		_: unknown,
											// 		row: LoggingType
											// 	) =>
											// 		getLoggingIsError(
											// 			row
											// 		)
											// 			? t('Yes')
											// 			: t('No')
											// },
											// {
											// 	title: t('Notify'),
											// 	key: 'notify',
											// 	width: 80,
											// 	render: (
											// 		_: unknown,
											// 		row: LoggingType
											// 	) =>
											// 		getLoggingNotify(
											// 			row
											// 		)
											// 			? t('Yes')
											// 			: t('No')
											// },
											{
												title: t('EventText'),
												dataIndex: 'text',
												key: 'text'
											}
										]}
									/>
								),
								rowExpandable: (record) =>
									record.count > 1
							}}
							pagination={{
								pageSize: 20,
								pageSizeOptions: [10, 20, 50, 100],
								showSizeChanger: true,
								showTotal: (total) =>
									`${t('Total')}: ${total}`,
								position: ['bottomCenter']
							}}
							scroll={{ x: 1200 }}
							columns={[
								{
									title: t('Count'),
									key: 'count',
									width: 70,
									fixed: 'left',
									align: 'center',
									render: (
										_: any,
										record: StackedLog
									) =>
										record.count > 1 ? (
											<Badge
												count={record.count}
												color={getTypeColor(
													record.type
												)}
												overflowCount={999}
											/>
										) : (
											<span
												style={{
													color: '#999'
												}}>
												{/* 1 */}
											</span>
										)
								},
								{
									title: '№',
									key: 'id',
									width: 70,
									fixed: 'left',
									align: 'center',
									render: (
										_: any,
										record: StackedLog
									) =>
										record.entries[0].lid ??
										record.entries[0].id
								},
								{
									title: t('Time'),
									key: 'lastOccurrence',
									width: 180,
									render: (
										_: any,
										record: StackedLog
									) => (
										<Tooltip
											title={
												record.count > 1
													? `${t('First')}: ${new Date(record.firstOccurrence).toLocaleString('ru')}`
													: new Date(
															record.lastOccurrence
														).toISOString()
											}>
											{new Date(
												record.lastOccurrence
											).toLocaleString('ru')}
										</Tooltip>
									),
									sorter: (
										a: StackedLog,
										b: StackedLog
									) =>
										new Date(
											b.lastOccurrence
										).getTime() -
										new Date(
											a.lastOccurrence
										).getTime()
									// defaultSortOrder: 'descend'
								},
								// {
								// 	title: t('Code'),
								// 	dataIndex: 'code',
								// 	key: 'code',
								// 	width: 130,
								// 	render: (
								// 		_: unknown,
								// 		record: StackedLog
								// 	) => (
								// 		<span
								// 			style={{
								// 				fontSize: '12px',
								// 				fontWeight: 600,
								// 				color: '#333'
								// 			}}>
								// 			{record.code || '-'}
								// 		</span>
								// 	)
								// },
								{
									title: t('Type'),
									dataIndex: 'type',
									key: 'type',
									width: 120,
									render: (type: string) => (
										<span
											style={{
												padding: '4px 12px',
												borderRadius: '4px',
												backgroundColor: `${getTypeColor(type || 'info')}20`,
												color: getTypeColor(
													type || 'info'
												),
												fontSize: '12px',
												fontWeight: 600
											}}>
											{type || '-'}
										</span>
									),
									filters: uniqueTypes.map(
										(type) => ({
											text: type,
											value: type
										})
									),
									onFilter: (value, record) =>
										record.type === value
								},
								{
									title: t('Source'),
									dataIndex: 'source',
									key: 'source',
									width: 145,
									render: (source: string) => (
										<span
											style={{
												fontSize: '12px',
												color: '#555'
											}}>
											{t(source) || '-'}
										</span>
									),
									filters: uniqueSources.map(
										(source) => ({
											text: t(source)
												? `${t(source)} (${source})`
												: source,
											value: source
										})
									),
									onFilter: (value, record) =>
										record.source === value
								},
								{
									title: t('EventText'),
									dataIndex: 'text',
									key: 'text',
									render: (text: string) => (
										<Tooltip title={text}>
											<span
												style={{
													cursor: 'pointer',
													color: '#1890ff'
												}}>
												{text.length > 50
													? text.substring(
															0,
															50
														) + '...'
													: text}
											</span>
										</Tooltip>
									)
								}
							]}
							size="middle"
							bordered={false}
							style={{ marginBottom: 0 }}
						/>
					)}
				</Spin>
			</div>
		</div>
	)
}

function getTypeColor(type: string): string {
	const colorMap: Record<string, string> = {
		error: '#ff4d4f',
		warning: '#faad14',
		info: '#1890ff',
		success: '#52c41a',
		debug: '#722ed1',
		trace: '#13c2c2'
	}
	return colorMap[type.toLowerCase()] || '#666'
}

export default Logging
